/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toSortParams } from '@data/dto/api.dto';
import { toQualification } from '@data/dto/qualification.dto';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import {
  Qualification,
  QualificationHttp,
  QualificationWizard,
} from '@data/models/qualification.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class QualificationService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly qualificationEndpoint = '/qualification-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listQualifications(
    filters: FilterParams,
  ): Promise<PaginateResult<Qualification[]>> {
    const { data: response, error } = await this.API?.get<{
      data: QualificationHttp[];
      meta: Meta;
    }>(this.qualificationEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toQualification) || [],
      totalSize: response?.meta.page.total || 0,
    };
  }

  public async upsertQualification({
    id,
    ...qualificationWizard
  }: QualificationWizard): Promise<Qualification | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<QualificationWizard, 'id'> },
        { data: QualificationHttp }
      >(this.qualificationEndpoint, { data: qualificationWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toQualification(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: QualificationWizard },
      { data: QualificationHttp }
    >(`${this.qualificationEndpoint}/${id}`, {
      data: { ...qualificationWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toQualification(data?.data || {});
  }

  public async deleteQualification(id: string): Promise<Qualification | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: QualificationHttp;
    }>(`${this.qualificationEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toQualification(data?.data || {});
  }

  public async sortQualification(
    sortParams: DatatableReorderParams<Qualification>,
  ): Promise<Qualification | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.qualificationEndpoint}/${id}/sort`,
      {
        data: toSortParams('qualification_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
